import { returnAllTradeDetailsFromGenericParams } from "@/lib/agGridColDefs/colDefGlobalHelpers";
import {
  containsHyphenInQuote,
  convertToDollarFraction,
  formatDollarFractionString,
  regexCheckFractionQuote,
} from "@/lib/businessRules/dollarFractionBusinessRule";
import { validateDollarFractionInput } from "@/lib/businessRules/dollarFractionInputValidation";
import { StrategyType } from "@/lib/interfaces and Enums/ENUMStrategyType";
import tradeDataStore from "@/store/tradeDataStore";
import { setNumericSetting } from "@/store/tradeSettingsStoreACTIONS";
import { truncateValueIfNeeded } from "../../cellEditors/DynamicCellEditorQuoteDollarFractionHelper";
import { useTheme } from "next-themes";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

/**
 * ReferencePriceLegCellEditor
 *
 * Cell editor for the reference price (Set Price) field.
 * Updates the referencePrice value in the legs array within tradeDataStore.
 */
const ReferencePriceLegCellEditor = forwardRef((params: any, ref) => {
  const currentData = params.data;
  const blockId = currentData.id;
  const notation = currentData.notation;
  const { theme } = useTheme();
  const { fullTradeData } = returnAllTradeDetailsFromGenericParams(params);

  const isUsingDollarFractionLogic = notation === "$fr";
  const inputType = isUsingDollarFractionLogic ? "text" : "number";

  const currentTradeData = tradeDataStore((state) => state.tradeData);
  const thisTrade = currentTradeData.find(
    (trade) => trade.esid === fullTradeData.esid,
  );

  // Get initial value from tradeDataStore legs for Outright (synced with settings)
  // This ensures editing starts with the current value, not stale row data
  const getInitialValue = () => {
    if (
      thisTrade?.strategyType === StrategyType.Outright &&
      thisTrade?.legs &&
      thisTrade.legs.length > 0
    ) {
      const matchingLeg = thisTrade.legs.find((leg) => leg.blockId === blockId);
      if (matchingLeg?.referencePrice !== undefined) {
        return matchingLeg.referencePrice.toString();
      }
    }
    return (params.value ?? "").toString();
  };

  const [value, setValue] = useState(getInitialValue);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;

    if (input === "") {
      setValue(input);
      return;
    }

    // $fr: use dollar fraction input validation
    if (isUsingDollarFractionLogic) {
      if (validateDollarFractionInput(input)) {
        setValue(input);
      }
      return;
    }

    // Non-$fr: allow negative sign, digits, and decimal point
    if (!/^-?\d*\.?\d*$/.test(input)) return;

    setValue(input);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      params.stopEditing();
    } else if (e.key === "Escape") {
      params.stopEditing(true);
    }
  };

  const validateAndSave = () => {
    if (!thisTrade || !thisTrade.legs || thisTrade.legs.length === 0) {
      return value === "" ? undefined : Number(value);
    }

    // Get the blockId of the leg being edited
    const blockIdToCheck = blockId;

    let finalValue: number | string | undefined;

    if (value === "") {
      finalValue = undefined;
    } else if (isUsingDollarFractionLogic) {
      // $fr: validate, convert, and format
      if (!regexCheckFractionQuote({ newValue: value })) {
        setValue("");
        finalValue = undefined;
      } else if (!containsHyphenInQuote(value)) {
        // User typed a decimal, convert to fraction format
        const truncated = truncateValueIfNeeded(value, notation);
        const numVal = Number(truncated);
        if (!isNaN(numVal)) {
          finalValue = formatDollarFractionString(convertToDollarFraction(numVal));
        } else {
          finalValue = truncated;
        }
      } else {
        // User typed a fraction — just pad formatting
        finalValue = formatDollarFractionString(value);
      }
    } else {
      // Non-$fr: apply rounding based on notation
      const roundedValue = truncateValueIfNeeded(value, notation).replace(
        /,/g,
        "",
      );
      finalValue = roundedValue !== "" ? Number(roundedValue) : undefined;
    }

    // Update the referencePrice on the matching leg
    const newThisTradeLegs = thisTrade.legs.map((leg) => {
      return leg.blockId === blockIdToCheck
        ? {
            ...leg,
            referencePrice: finalValue,
            notation: notation,
          }
        : leg;
    });

    const newThisTrade = { ...thisTrade, legs: newThisTradeLegs };

    const newData = currentTradeData.map((trade) => {
      return trade.esid === fullTradeData.esid ? newThisTrade : trade;
    });

    tradeDataStore.getState().updateTradeData(newData);

    // Sync with settings panel for Outright trades
    if (thisTrade.strategyType === StrategyType.Outright) {
      setNumericSetting({
        esid: fullTradeData.esid,
        settingField: "orderPriceValue",
        value: finalValue,
      });
    }

    return finalValue;
  };

  useImperativeHandle(ref, () => ({
    getValue: () => validateAndSave(),
  }));

  return (
    <input
      ref={inputRef}
      value={value}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      className={`${
        theme === "light"
          ? "inputCellOtherLight inputCellLight"
          : "inputCellOtherDark inputCellDark"
      } ag-input ag-roundedInputStyle w-full justify-center !border-none text-center`}
      type={inputType}
      step={isUsingDollarFractionLogic ? undefined : "any"}
    />
  );
});

export default ReferencePriceLegCellEditor;
